import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
// Navigation uses window.location.href for reliability (bypasses React Router batching)
import {
  Bell,
  BellRing,
  Clock,
  Zap,
  CheckCheck,
  X,
  Copy,
  FileSearch,
  Radar,
} from "lucide-react";
import { toast } from "sonner";
import { useAlerts } from "@/context/AlertContext";

// Icon by alert type
const TIPO_CONFIG = {
  plazo_urgente: {
    icon: Clock,
    color: "text-red-400",
    bg: "bg-red-400/10",
    label: "Plazo urgente",
  },
  nueva_alta_prioridad: {
    icon: Zap,
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    label: "Alta prioridad",
  },
  cambio_estado: {
    icon: Bell,
    color: "text-blue-400",
    bg: "bg-blue-400/10",
    label: "Cambio estado",
  },
};

// Origin config — determines navigation target and visual indicator
const ORIGEN_CONFIG = {
  licitacion: {
    icon: FileSearch,
    color: "text-emerald-400",
    label: "Licitación",
    route: "/licitaciones",
  },
  oportunidad: {
    icon: Radar,
    color: "text-cyan-400",
    label: "Oportunidad",
    route: "/oportunidades",
  },
};

function AlertItem({ alert, onRead, onCopy, onNavigate }) {
  const config = TIPO_CONFIG[alert.tipo] || TIPO_CONFIG.cambio_estado;
  const Icon = config.icon;
  const origen = alert.origen || "licitacion";
  const origenConfig = ORIGEN_CONFIG[origen] || ORIGEN_CONFIG.licitacion;
  const OrigenIcon = origenConfig.icon;
  const ref = alert.metadata?.expediente_id || alert.licitacion_id || "";

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "ahora";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    return `hace ${Math.floor(diff / 86400)}d`;
  };

  return (
    <div
      className={`px-3 py-2.5 transition-colors border-b ${
        alert.leida ? "opacity-60" : ""
      }`}
      style={{ borderColor: "var(--theme-border)" }}
    >
      <div className="flex items-start gap-2.5">
        <div className={`p-1.5 rounded-md ${config.bg} flex-shrink-0 mt-0.5`}>
          <Icon className={`w-3.5 h-3.5 ${config.color}`} />
        </div>
        <div
          className="flex-1 min-w-0 cursor-pointer hover:opacity-80"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={() => {
            if (!alert.leida) onRead(alert._id);
            onNavigate(alert);
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-medium ${config.color}`}>
                {config.label}
              </span>
              <span
                className={`text-[9px] ${origenConfig.color} flex items-center gap-0.5 opacity-70`}
              >
                <OrigenIcon className="w-2.5 h-2.5" />
                {origenConfig.label}
              </span>
            </div>
            <span className="text-[10px] theme-text-muted flex-shrink-0">
              {timeAgo(alert.created_at)}
            </span>
          </div>
          <p className="text-xs font-medium theme-text mt-0.5 truncate">
            {alert.titulo}
          </p>
          <p className="text-[11px] theme-text-muted mt-0.5 line-clamp-2">
            {alert.mensaje}
          </p>
        </div>
        <div className="flex flex-col items-center gap-1 flex-shrink-0 mt-1">
          {!alert.leida && (
            <div className="w-2 h-2 rounded-full bg-cyan-400" />
          )}
          <button
            className="p-1 rounded hover:bg-slate-700/50 transition-colors"
            title={ref ? `Copiar ref: ${ref}` : "Sin referencia"}
            onMouseDown={(e) => {
              // Prevent mousedown from bubbling to document (handleClickOutside)
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCopy(ref);
            }}
          >
            <Copy className="w-3 h-3 text-slate-500 hover:text-cyan-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AlertsDropdown() {
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);
  const {
    alertCount,
    alerts,
    isOpen,
    loading,
    toggleDropdown,
    closeDropdown,
    markAsRead,
    markAllAsRead,
  } = useAlerts();

  // Calculate panel position when opening
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPanelPos({
      bottom: window.innerHeight - rect.top + 8,
      left: Math.max(8, rect.left),
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore temporary textareas created by clipboard fallback
      if (e.target.tagName === "TEXTAREA" && e.target.style.position === "fixed") return;
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        closeDropdown();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeDropdown]);

  const handleNavigate = (alert) => {
    const origen = alert.origen || "licitacion";
    const config = ORIGEN_CONFIG[origen] || ORIGEN_CONFIG.licitacion;
    const recordId = alert.licitacion_id;
    closeDropdown();
    // Always use window.location.href — bypasses React Router batching/unmount race conditions
    if (recordId) {
      window.location.href = `${config.route}?open=${recordId}`;
    } else {
      window.location.href = config.route;
    }
  };

  const handleCopy = (ref) => {
    if (!ref) {
      toast.error("Sin referencia");
      return;
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = ref;
      ta.style.cssText = "position:fixed;left:-9999px;top:-9999px;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`Ref copiada: ${ref}`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <>
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="relative p-2 rounded-lg theme-text-secondary hover:text-cyan-400 hover:bg-cyan-400/10 transition-colors"
        title={`${alertCount} alertas sin leer`}
      >
        {alertCount > 0 ? (
          <BellRing className="w-5 h-5 text-cyan-400 animate-pulse" />
        ) : (
          <Bell className="w-5 h-5" />
        )}

        {/* Badge */}
        {alertCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-lg">
            {alertCount > 99 ? "99+" : alertCount}
          </span>
        )}
      </button>

      {/* Dropdown panel — rendered via portal to escape sidebar overflow */}
      {isOpen && panelPos && createPortal(
        <div
          ref={panelRef}
          className="fixed w-80 sm:w-96 theme-bg-tertiary rounded-xl shadow-2xl border z-[100] overflow-hidden"
          style={{
            borderColor: "var(--theme-border)",
            bottom: panelPos.bottom,
            left: panelPos.left,
          }}
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between border-b"
            style={{ borderColor: "var(--theme-border)" }}
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold theme-text">Alertas</span>
              {alertCount > 0 && (
                <span className="bg-cyan-400/20 text-cyan-400 text-[10px] font-bold rounded-full px-2 py-0.5">
                  {alertCount} nuevas
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {alertCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-1.5 rounded-md hover:bg-cyan-400/10 transition-colors"
                  title="Marcar todas como leidas"
                >
                  <CheckCheck className="w-4 h-4 theme-text-muted hover:text-cyan-400" />
                </button>
              )}
              <button
                onClick={closeDropdown}
                className="p-1.5 rounded-md hover:bg-red-400/10 transition-colors"
              >
                <X className="w-4 h-4 theme-text-muted hover:text-red-400" />
              </button>
            </div>
          </div>

          {/* Alert list */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <div className="text-cyan-400 animate-pulse text-sm">
                  Cargando alertas...
                </div>
              </div>
            ) : alerts.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 theme-text-muted mx-auto mb-2 opacity-40" />
                <p className="text-sm theme-text-muted">Sin alertas</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <AlertItem
                  key={alert._id}
                  alert={alert}
                  onRead={markAsRead}
                  onCopy={handleCopy}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div
              className="px-4 py-2 text-center border-t"
              style={{ borderColor: "var(--theme-border)" }}
            >
              <span className="text-[11px] theme-text-muted">
                Mostrando las {alerts.length} mas recientes
              </span>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
